import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, HelpCircle, Search } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Kbd } from "@/components/common/bits";
import { Hint } from "@/components/help/InfoTip";
import { getArticle, getPageHelp, helpKeyFor } from "@/content/help";
import { getModule } from "@/config/modules";

// Header "?" button. Opens a drawer with what the current page is for, the usual
// steps, tips, and related guides. Pressing "?" anywhere (outside a text field)
// opens it too.
export function HelpButton() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const key = helpKeyFor(pathname, search);
  const help = getPageHelp(key);
  const fallbackTitle = key === "home" ? "Home" : getModule(key)?.title ?? "This page";

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "?" || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable)) return;
      e.preventDefault();
      setOpen(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const close = () => setOpen(false);
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!q.trim()) return;
    close();
    navigate(`/help?q=${encodeURIComponent(q.trim())}`);
    setQ("");
  };
  const related = (help?.articles ?? []).map(getArticle).filter((a): a is NonNullable<typeof a> => !!a);

  return (
    <>
      <Hint tip={<span className="flex items-center gap-1.5">Help for this page <Kbd>?</Kbd></span>}>
        <Button variant="ghost" size="icon" onClick={() => setOpen(true)} aria-label="Help">
          <HelpCircle className="size-4" />
        </Button>
      </Hint>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="sm:max-w-md">
          <div className="border-b border-border p-5 pr-12">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Help</p>
            <h2 className="mt-1 font-display text-xl font-bold tracking-tight">{help?.title ?? fallbackTitle}</h2>
            <form onSubmit={submit} className="relative mt-3">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search all help" className="h-9 pl-8" />
            </form>
          </div>

          <div className="flex-1 space-y-5 overflow-y-auto p-5">
            {help ? (
              <>
                <p className="text-sm leading-relaxed text-muted-foreground">{help.summary}</p>
                {help.steps.length > 0 && (
                  <section>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">How to use this page</h3>
                    <ol className="space-y-2.5">
                      {help.steps.map((s, i) => (
                        <li key={i} className="flex gap-3">
                          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/12 text-xs font-semibold text-primary">{i + 1}</span>
                          <div>
                            <p className="text-sm font-medium">{s.title}</p>
                            {s.body && <p className="text-sm leading-relaxed text-muted-foreground">{s.body}</p>}
                          </div>
                        </li>
                      ))}
                    </ol>
                  </section>
                )}
                {help.tips && help.tips.length > 0 && (
                  <section>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tips</h3>
                    <ul className="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-muted-foreground">
                      {help.tips.map((t, i) => <li key={i}>{t}</li>)}
                    </ul>
                  </section>
                )}
                {related.length > 0 && (
                  <section>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Related guides</h3>
                    <ul className="space-y-1">
                      {related.map((a) => (
                        <li key={a.id}>
                          <Link to={`/help/${a.id}`} onClick={close} className="group flex items-start gap-2 rounded-md p-2 hover:bg-accent/50">
                            <BookOpen className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                            <span className="min-w-0">
                              <span className="block text-sm font-medium group-hover:text-primary">{a.title}</span>
                              <span className="block text-xs text-muted-foreground">{a.summary}</span>
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </>
            ) : (
              <p className="text-sm leading-relaxed text-muted-foreground">
                There's no page-specific help for this screen yet. Search above, or browse the Help Center.
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 border-t border-border p-4">
            <Button asChild size="sm" onClick={close}><Link to="/help">Open Help Center <ArrowRight className="size-3.5" /></Link></Button>
            <Button asChild variant="ghost" size="sm" onClick={close}><Link to="/help/welcome">New here? Start here</Link></Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
